<template>
  <div class="bar-chart-container pt-5 pr-5">
    <div class="chart-scroll-wrapper">
      <BarChartGenerator
        :chart-options="chartOptions"
        :chart-data="chartData"
        :chart-id="chartId"
        :dataset-id-key="datasetIdKey"
        :plugins="plugins"
        :css-classes="cssClasses"
        :styles="chartStyles"
        :height="height"
      />
    </div>

    <!-- Custom Legend -->
    <div class="custom-legend">
      <div
        v-for="(dataset, index) in chartData.datasets"
        :key="index"
        class="legend-item"
      >
        <div
          class="legend-box"
          :style="{ backgroundColor: dataset.backgroundColor }"
        ></div>
        <span class="legend-label">{{ dataset.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar as BarChartGenerator } from "vue-chartjs/legacy";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale
);

export default {
  name: "BarChart",
  components: {
    BarChartGenerator,
  },
  props: {
    chartId: {
      type: String,
      default: "bar-chart",
    },
    datasetIdKey: {
      type: String,
      default: "label",
    },
    width: {
      type: Number,
      default: undefined,
    },
    height: {
      type: Number,
      default: 300,
    },
    cssClasses: {
      default: "",
      type: String,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    plugins: {
      type: Array,
      default: () => [],
    },
    passfail: {
      type: Array,
      required: true,
    },
    notAttempted: {
      type: Array,
      required: true,
    },
    passfailPercent: {
      type: Array,
      required: true,
    },
    notAttemptedPercent: {
      type: Array,
      required: true,
    },
    schoolLabels: {
      type: Array,
      required: true,
    },
  },
  computed: {
    chartStyles() {
      const minWidthPerLabel = 90; // px per school (tweak if needed)
      return {
        minWidth: `${this.schoolLabels.length * minWidthPerLabel}px`,
        height: "100%",
      };
    },
    chartData() {
      // ✅ Store original labels
      this.originalLabels = this.schoolLabels;

      return {
        labels: this.schoolLabels.map((label, index) => {
          const total = this.passfail[index]?.total ?? 0;

          const [code, rest] = label.split("-");

          // No hyphen case
          if (!rest) {
            return [`${label.trim()} (${total})`];
          }

          const words = rest.trim().split(" ");

          return [
            `${code.trim()} -`,
            ...words.slice(0, -1),
            `${words[words.length - 1]} (${total})`, // ✅ same last line
          ];
        }),

        datasets: [
          {
            label: "Pass",
            backgroundColor: "#5AD684CC",
            stack: "Stack 0",
            barThickness: "flex",
            // maxBarThickness: 70,
            data: this.passfailPercent.map((item) => item.passPercentage),
          },
          {
            label: "Fail",
            backgroundColor: "#FF78A0CC",
            stack: "Stack 0",
            barThickness: "flex",
            // maxBarThickness: 70,
            data: this.passfailPercent.map((item) => item.failPercentage),
          },
          
          {
            label: "Not Attempted",
            backgroundColor: "#A3A3A399",
            stack: "Stack 1",
            barThickness: "flex",
            // maxBarThickness: 70,
            data: this.notAttemptedPercent.map((item) => item.notAttemptedPercentage),
          },
          {
            label: "In Progress",
            backgroundColor: "#FFB86B",
            stack: "Stack 1",
            barThickness: "flex",
            // maxBarThickness: 70,
            data: this.notAttemptedPercent.map((item) => item.inProgressPercentage),
          },
        ],
      };
    },
  },
  mounted() {
    this.updateFontSizeForBreakpoint();
    window.addEventListener("resize", this.updateFontSizeForBreakpoint);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateFontSizeForBreakpoint);
  },
  methods: {
    updateFontSizeForBreakpoint() {
      const width = window.innerWidth;
      const isSmall = width <= 1380;
      this.chartOptions.plugins.datalabels.font.size = isSmall ? 10 : 12;
    },
  },
  data() {
    return {
      originalLabels: [],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              title: (context) => {
                // ✅ Use original label for tooltip title
                return this.originalLabels[context[0].dataIndex];
              },
              label: (context) => {
              const datasetIndex = context.datasetIndex;
              const index = context.dataIndex;
              const percent = context.parsed.y;

              let count = 0;

              switch (context.dataset.label) {
                case "Pass":
                  count = this.passfail[index]?.pass ?? 0;
                  break;

                case "Fail":
                  count = this.passfail[index]?.fail ?? 0;
                  break;

                case "In Progress":
                  count = this.notAttempted[index]?.inProgress ?? 0;
                  break;

                case "Not Attempted":
                  count = this.notAttempted[index]?.notAttempted ?? 0;
                  break;
              }

                return `${context.dataset.label}: ${count} (${percent}%)`;
            }

            },
          },

          datalabels: {
            anchor: "center",
            align: "center",
            offset: -20,
            font: { size: 12 },
            color: "#000",
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            categoryPercentage: 1.0,
            barPercentage: 1.0,
            ticks: {
              maxRotation: 0, // ✅ prevent vertical rotation
              minRotation: 0, // ✅ keep horizontal
              autoSkip: false, // ✅ show all multiline labels
              align: "center",
            },
          },
          y: {
            beginAtZero: true,
            stacked: true,
            grid: { display: false },
          },
        },
      },
    };
  },
};
</script>

<style scoped>
.bar-chart-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  margin: auto;
  position: relative;
  display: flex;
  flex-direction: column;
}

.custom-legend {
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 16px 0;
  flex-wrap: wrap;
  margin-top: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.legend-box {
  width: 20px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-label {
  font-size: 12px;
  font-weight: 500 !important;
  font-family: "GraphikMedium", sans-serif !important;
  color: #333;
}
.chart-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
}

/* Optional: smoother scrolling */
.chart-scroll-wrapper::-webkit-scrollbar {
  height: 6px;
}

.chart-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}
</style>
